import React from 'react';
import { Select } from 'antd';
import PersonnelSelect from '../../modals/PersonnelSelect';

import { Field, resolveFieldTextAlign } from './constants';
import FormattedNumberInput from './FormattedNumberInput';

interface FormFieldRendererProps {
  fields: Field[];
  fieldValues: {[key: string]: string | boolean};
  selectedIdxs: Set<number>;
  focusedIdx: number | null;
  hoverIdx: number | null;
  duplicateNames: string[];
  resizing: { idx: number } | null;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onFieldNameIconClick: (idx: number) => void;
  onMouseEnter: (idx: number) => void;
  onMouseLeave: (idx: number) => void;
  onToggleSelect: (e: React.MouseEvent, idx: number) => void;
  onNudgeKey: (idx: number) => (e: React.KeyboardEvent) => void;
  onStartDrag: (idx: number, e: React.MouseEvent) => void;
  onStartResize: (idx: number, handle: any, e: React.MouseEvent) => void;
  onContextMenu: (e: React.MouseEvent, idx: number) => void;
  imgSize: { width: number; height: number };
  onSetFieldDefault?: (name: string, value: string) => void; // optional hook to write into field definition
}

const FormFieldRenderer: React.FC<FormFieldRendererProps> = ({
  fields,
  fieldValues,
  selectedIdxs,
  focusedIdx,
  hoverIdx,
  duplicateNames,
  resizing,
  onInputChange,
  onFieldNameIconClick,
  onMouseEnter,
  onMouseLeave,
  onToggleSelect,
  onNudgeKey,
  onStartDrag,
  onStartResize,
  onContextMenu,
  imgSize,
  onSetFieldDefault,
}) => {
  const duplicateBorderColor = '#faad14';
  const baseOverlayBg = 'rgba(230,242,255,0.2)';
  const selectedOverlayBg = 'rgba(22,119,255,0.18)';

  const sigClickTimerRef = React.useRef<number | null>(null);

  const cancelSigSingleClick = () => {
    if (sigClickTimerRef.current !== null) {
      window.clearTimeout(sigClickTimerRef.current);
      sigClickTimerRef.current = null;
    }
  };

  const handleOverlayDoubleClick = (idx: number) => (e: React.MouseEvent) => {
    cancelSigSingleClick();
    e.preventDefault();
    e.stopPropagation();
    onFieldNameIconClick(idx);
  };

  // Local state for signature selection modal
  const [sigOpen, setSigOpen] = React.useState(false);
  const [sigFieldIdx, setSigFieldIdx] = React.useState<number | null>(null);

  const getInputValue = (val: unknown, fallback: string | number = ''): string | number => {
    return typeof val === 'string' || typeof val === 'number' ? val : fallback;
  };

  const normalizeOption = (o: string | { value: string; label: string }) => {
    if (typeof o === 'string') return { value: o, label: o };
    return { value: String(o?.value ?? ''), label: String(o?.label ?? o?.value ?? '') };
  };

  const getContainerPoint = (e: React.MouseEvent): { x: number; y: number } => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  return (
    <>
      {fields.map((field, idx) => {
        if (!field.position) return null;
        const { x0, y0, x1, y1 } = field.position;
        const left = x0;
        const top = y0;
        const width = x1 - x0;
        const height = y1 - y0;
        const fontSize = field.fontsize || 12;
        const textAlign = resolveFieldTextAlign(field.textAlign);
        const isDuplicate = duplicateNames.includes((field.name || '').trim().toLowerCase());
        const isFocused = focusedIdx === idx;
        const adjustedBox = isDuplicate
          ? {
              left: x0 - 2,
              top: y0 - 2,
              width: width + 4,
              height: height + 4
            }
          : { left: x0, top: y0, width, height };
        const isSelected = selectedIdxs.has(idx);

        if (field.type === 'checkbox') {
          const containerWidth = Math.max(adjustedBox.width, 40);
          
          return (
            <div
              key={idx}
              style={{
                position: 'absolute',
                left: adjustedBox.left,
                top: adjustedBox.top,
                width: containerWidth,
                height: adjustedBox.height,
                pointerEvents: 'auto',
                background: isSelected ? selectedOverlayBg : baseOverlayBg,
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                zIndex: isDuplicate ? 10 : (isFocused ? 25 : (isSelected ? 20 : undefined)),
                border: isDuplicate ? `2px solid ${duplicateBorderColor}` : (isSelected ? '1px solid #1677ff' : '1px solid #91caff'),
                outline: undefined,
                padding: '0 2px',
                boxSizing: 'border-box',
                gap: 4
              }}
              onMouseEnter={() => onMouseEnter(idx)}
              onMouseLeave={() => onMouseLeave(idx)}
              onMouseDown={(e) => { onToggleSelect(e, idx); (e.currentTarget as HTMLDivElement).focus(); }}
              onDoubleClickCapture={handleOverlayDoubleClick(idx)}
              tabIndex={0}
              onKeyDown={onNudgeKey(idx)}
              onContextMenu={(e) => onContextMenu(e, idx)}
            >
              <div style={{ 
                position: 'relative', 
                width: '12px', 
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0 
              }}>
                <div
                  title="Drag to reposition"
                  onMouseDown={(e) => { e.stopPropagation(); onStartDrag(idx, e); }}
                  style={{ 
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    cursor: 'move',
                    zIndex: 2,
                    opacity: 0.3,
                    background: 'transparent',
                    pointerEvents: 'auto'
                  }}
                />
                <input
                  id={field.name}
                  name={field.name}
                  type="checkbox"
                  checked={!!fieldValues[field.name]}
                  onChange={onInputChange}
                  onFocus={() => onMouseEnter(idx)}
                  onBlur={() => onMouseLeave(idx)}
                  onKeyDown={onNudgeKey(idx)}
                  style={{ 
                    width: '100%',
                    height: '100%',
                    margin: 0,
                    position: 'relative',
                    zIndex: 1
                  }}
                  placeholder={field.name}
                />
              </div>
              
              <span
                style={{
                  flex: 1,
                  minWidth: 2,
                  fontSize,
                  lineHeight: 1.1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  userSelect: 'none',
                }}
                title={(field.label || field.name || '').toString()}
              >
                {(field.label || field.name || '').toString()}
              </span>

              {resizing && resizing.idx === idx && (
                <div style={{ 
                  position: 'absolute', 
                  right: 0, 
                  bottom: '100%', 
                  marginBottom: 4, 
                  background: 'rgba(0,0,0,0.65)', 
                  color: '#fff', 
                  fontSize: 10, 
                  padding: '2px 4px', 
                  borderRadius: 3, 
                  pointerEvents: 'none' 
                }}>
                  {Math.round(containerWidth)}×{Math.round(adjustedBox.height)}
                </div>
              )}
              {selectedIdxs.size <= 1 && isSelected && (
                <div
                  title="Resize"
                  onMouseDown={(e) => onStartResize(idx, 'se', e)}
                  style={{ position:'absolute', right:0, bottom:0, width:24, height:24, cursor:'nwse-resize', zIndex:12, pointerEvents:'auto', background:'transparent' }}
                />
              )}
            </div>
          );
        }

        const value = fieldValues[field.name] ?? field.defaultValue ?? '';
        const safeValue = typeof value === 'boolean' ? '' : value;

        // Inline horizontal radio preview in designer
        if (field.type === 'radio') {
          const opts = (Array.isArray(field.options) ? field.options : [])
            .map(normalizeOption)
            .filter(o => !!o.value);
          const valueStr = typeof safeValue === 'string' ? safeValue : '';
          const shown = opts.slice(0, 4);
          const radioScale = Math.max(0.8, Math.min(2, fontSize / 12));
          return (
            <div
              data-field-overlay
              key={idx}
              style={{
                position: 'absolute',
                left: adjustedBox.left,
                top: adjustedBox.top,
                width: adjustedBox.width,
                height: adjustedBox.height,
                pointerEvents: 'auto',
                background: isSelected ? selectedOverlayBg : baseOverlayBg,
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: isDuplicate ? 10 : (isFocused ? 25 : (isSelected ? 20 : undefined)),
                border: isDuplicate ? `2px solid ${duplicateBorderColor}` : (isSelected ? '1px solid #1677ff' : '1px solid #91caff'),
                outline: undefined,
                padding: 0,
              }}
              onMouseEnter={() => onMouseEnter(idx)}
              onMouseLeave={() => onMouseLeave(idx)}
              onMouseDown={(e) => { onToggleSelect(e, idx); (e.currentTarget as HTMLDivElement).focus(); }}
              onDoubleClickCapture={handleOverlayDoubleClick(idx)}
              tabIndex={0}
              onKeyDown={onNudgeKey(idx)}
              onContextMenu={(e) => onContextMenu(e, idx)}
            >
              <div
                title="Drag to reposition"
                onMouseDown={(e) => { onStartDrag(idx, e); e.stopPropagation(); }}
                style={{ position: 'absolute', top: 2, left: 2, width: 12, height: 12, cursor: 'move', zIndex: 12, borderRadius: 2, background: 'rgba(0,0,0,0.15)' }}
              />
              <div style={{ width: '100%', height: '100%', display: 'flex', alignItems:'center', justifyContent:'space-evenly', padding: 4, gap: 0, overflow: 'hidden' }}>
                {(shown.length > 0 ? shown : [{ value: '', label: field.name }]).map((o, k) => {
                  const checked = !!o.value && valueStr === o.value;
                  return (
                    <label key={`${o.value}-${k}`} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 3, cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', flex: '1 1 0' }}>
                      <input
                        type="radio"
                        name={field.name}
                        checked={checked}
                        style={{ margin: 0, transform: `scale(${radioScale})`, transformOrigin: 'center', pointerEvents: 'none' }}
                      />
                      <span style={{ fontSize, overflow: 'hidden', textOverflow: 'ellipsis' }}>{o.label || o.value}</span>
                    </label>
                  );
                })}
              </div>
              {selectedIdxs.size <= 1 && isSelected && (
                <div
                  title="Resize"
                  onMouseDown={(e) => onStartResize(idx, 'se', e)}
                  style={{ position:'absolute', right:0, bottom:0, width: Math.max(10, Math.min(16, Math.floor(Math.min(width, height)))) , height: Math.max(10, Math.min(16, Math.floor(Math.min(width, height)))) , cursor:'nwse-resize', zIndex:12, pointerEvents:'auto', background:'transparent' }}
                />
              )}
            </div>
          );
        }
        
        // Special interactive behavior for signature: open personnel selector (multi) and store selected codes list
        if (field.type === 'signature') {
          const raw = fieldValues[field.name] ?? field.defaultValue ?? '';
          let codes: string[] = [];
          if (typeof raw === 'string') {
            try { const parsed = JSON.parse(raw); if (Array.isArray(parsed)) codes = parsed.map(String); } catch { /* ignore */ }
          }
          const display = codes.length > 0 ? codes.join(', ') : '';
          return (
            <div
              data-field-overlay
              key={idx}
              style={{
                position: 'absolute',
                left: adjustedBox.left,
                top: adjustedBox.top,
                width: adjustedBox.width,
                height: adjustedBox.height,
                pointerEvents: 'auto',
                background: isSelected ? selectedOverlayBg : baseOverlayBg,
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: isDuplicate ? 10 : (isFocused ? 25 : (isSelected ? 20 : undefined)),
                border: isDuplicate ? `2px solid ${duplicateBorderColor}` : (isSelected ? '1px solid #1677ff' : '1px solid #91caff'),
                outline: undefined,
                cursor: 'pointer'
              }}
              onMouseEnter={() => onMouseEnter(idx)}
              onMouseLeave={() => onMouseLeave(idx)}
              onMouseDown={(e) => { onToggleSelect(e, idx); (e.currentTarget as HTMLDivElement).focus(); }}
              onDoubleClickCapture={handleOverlayDoubleClick(idx)}
              tabIndex={0}
              onKeyDown={onNudgeKey(idx)}
              onContextMenu={(e) => onContextMenu(e, idx)}
              onClick={(e) => {
                e.stopPropagation();
                cancelSigSingleClick();
                sigClickTimerRef.current = window.setTimeout(() => {
                  setSigFieldIdx(idx);
                  setSigOpen(true);
                  sigClickTimerRef.current = null;
                }, 200);
              }}
            >
              <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                <div
                  title="Drag to reposition"
                  onMouseDown={(e) => { e.stopPropagation(); onStartDrag(idx, e); }}
                  style={{ position: 'absolute', top: 2, left: 2, width: 12, height: 12, cursor: 'move', zIndex: 12, borderRadius: 2, background: 'rgba(0,0,0,0.15)' }}
                />
              </div>
              <div style={{ width: '100%', height: '100%', display: 'flex', alignItems:'center', justifyContent:'center', padding: 4 }}>
                <span style={{ fontSize, color: display ? '#000' : '#999' }}>
                  {display || 'Click to select signatories'}
                </span>
              </div>
              {selectedIdxs.size <= 1 && isSelected && (
                <div
                  title="Resize"
                  onMouseDown={(e) => onStartResize(idx, 'se', e)}
                  style={{ position:'absolute', right:0, bottom:0, width: Math.max(10, Math.min(16, Math.floor(Math.min(width, height)))) , height: Math.max(10, Math.min(16, Math.floor(Math.min(width, height)))) , cursor:'nwse-resize', zIndex:12, pointerEvents:'auto', background:'transparent' }}
                />
              )}
            </div>
          );
        }

        // Special non-editable display for submitted_by in designer
        if (field.type === 'submitted_by') {
          const mode = field.defaultValue === 'single_line' ? 'single_line' : 'two_line';
          const display = mode === 'single_line' ? 'Name Surname — YYYY-MM-DD' : 'Name Surname\nYYYY-MM-DD';
          return (
            <div
              data-field-overlay
              key={idx}
              style={{
                position: 'absolute',
                left: adjustedBox.left,
                top: adjustedBox.top,
                width: adjustedBox.width,
                height: adjustedBox.height,
                pointerEvents: 'auto',
                background: isSelected ? selectedOverlayBg : baseOverlayBg,
                borderRadius: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: isDuplicate ? 10 : (isFocused ? 25 : (isSelected ? 20 : undefined)),
                border: isDuplicate ? `2px solid ${duplicateBorderColor}` : (isSelected ? '1px solid #1677ff' : '1px solid #91caff'),
                outline: undefined,
              }}
              onMouseEnter={() => onMouseEnter(idx)}
              onMouseLeave={() => onMouseLeave(idx)}
              onMouseDown={(e) => { onToggleSelect(e, idx); (e.currentTarget as HTMLDivElement).focus(); }}
              onDoubleClickCapture={handleOverlayDoubleClick(idx)}
              tabIndex={0}
              onKeyDown={onNudgeKey(idx)}
              onContextMenu={(e) => onContextMenu(e, idx)}
            >
              <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                <div
                  title="Drag to reposition"
                  onMouseDown={(e) => onStartDrag(idx, e)}
                  style={{ position: 'absolute', top: 2, left: 2, width: 12, height: 12, cursor: 'move', zIndex: 12, borderRadius: 2, background: 'rgba(0,0,0,0.15)' }}
                />
              </div>
              <div style={{ width: '100%', height: '100%', display: 'flex', alignItems:'center', justifyContent:'center', padding: 4 }}>
                <span style={{ fontSize, color: '#999', whiteSpace: 'pre-wrap', textAlign: 'center' }}>{display}</span>
              </div>
              {resizing && resizing.idx === idx && (
                <div style={{ 
                  position: 'absolute', 
                  right: 0, 
                  bottom: '100%', 
                  marginBottom: 4, 
                  background: 'rgba(0,0,0,0.65)', 
                  color: '#fff', 
                  fontSize: 10, 
                  padding: '2px 4px', 
                  borderRadius: 3, 
                  pointerEvents: 'none' 
                }}>
                  {Math.round(width)}×{Math.round(height)}
                </div>
              )}
              {selectedIdxs.size <= 1 && isSelected && (
                <div
                  title="Resize"
                  onMouseDown={(e) => onStartResize(idx, 'se', e)}
                  style={{ position:'absolute', right:0, bottom:0, width: Math.max(10, Math.min(16, Math.floor(Math.min(width, height)))) , height: Math.max(10, Math.min(16, Math.floor(Math.min(width, height)))) , cursor:'nwse-resize', zIndex:12, pointerEvents:'auto', background:'transparent' }}
                />
              )}
            </div>
          );
        }

        // Inline dropdown preview for select/dropdown fields in designer
        if (
          field.type === 'dropdown' ||
          (field.type === 'select' && (field.modalComponent === 'InlineModal' || !field.modalComponent))
        ) {
          const opts = Array.isArray(field.options) ? field.options : [];
          const valueStr = typeof safeValue === 'string' ? safeValue : '';
          return (
            <div
              data-field-overlay
              key={idx}
              style={{
                position: 'absolute',
                left: adjustedBox.left,
                top: adjustedBox.top,
                width: adjustedBox.width,
                height: adjustedBox.height,
                pointerEvents: 'auto',
                background: isSelected ? selectedOverlayBg : baseOverlayBg,
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: isDuplicate ? 10 : (isFocused ? 25 : (isSelected ? 20 : undefined)),
                border: isDuplicate ? `2px solid ${duplicateBorderColor}` : (isSelected ? '1px solid #1677ff' : '1px solid #91caff'),
                outline: undefined,
              }}
              onMouseEnter={() => onMouseEnter(idx)}
              onMouseLeave={() => onMouseLeave(idx)}
              onMouseDown={(e) => { onToggleSelect(e, idx); (e.currentTarget as HTMLDivElement).focus(); }}
              onDoubleClickCapture={handleOverlayDoubleClick(idx)}
              tabIndex={0}
              onKeyDown={onNudgeKey(idx)}
              onContextMenu={(e) => onContextMenu(e, idx)}
            >
              <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                <div
                  title="Drag to reposition"
                  onMouseDown={(e) => { onStartDrag(idx, e); e.stopPropagation(); }}
                  style={{ position: 'absolute', top: 2, left: 2, width: 12, height: 12, cursor: 'move', zIndex: 12, borderRadius: 2, background: 'rgba(0,0,0,0.15)' }}
                />
              </div>
              <div style={{ width: '100%', height: '100%', display: 'flex', alignItems:'center', justifyContent:'center', padding: 4 }}>
                <Select
                  size="small"
                  style={{ width: '100%', pointerEvents: 'none' }}
                  value={valueStr || undefined}
                  placeholder={field.name}
                  options={opts.map(normalizeOption)}
                />
              </div>
              {selectedIdxs.size <= 1 && isSelected && (
                <div
                  title="Resize"
                  onMouseDown={(e) => onStartResize(idx, 'se', e)}
                  style={{ position:'absolute', right:0, bottom:0, width: Math.max(10, Math.min(16, Math.floor(Math.min(width, height)))) , height: Math.max(10, Math.min(16, Math.floor(Math.min(width, height)))) , cursor:'nwse-resize', zIndex:12, pointerEvents:'auto', background:'transparent' }}
                />
              )}
            </div>
          );
        }

        return (
          <div
            data-field-overlay
            key={idx}
            style={{
              position: 'absolute',
              left: adjustedBox.left,
              top: adjustedBox.top,
              width: adjustedBox.width,
              height: adjustedBox.height,
              pointerEvents: 'auto',
              background: isSelected ? selectedOverlayBg : baseOverlayBg,
              borderRadius: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: isDuplicate ? 10 : (isFocused ? 25 : (isSelected ? 20 : undefined)),
              border: isDuplicate ? `2px solid ${duplicateBorderColor}` : (isSelected ? '1px solid #1677ff' : '1px solid #91caff'),
              outline: undefined,
            }}
            onMouseEnter={() => onMouseEnter(idx)}
            onMouseLeave={() => onMouseLeave(idx)}
            onMouseDown={(e) => { onToggleSelect(e, idx); (e.currentTarget as HTMLDivElement).focus(); }}
            onDoubleClickCapture={handleOverlayDoubleClick(idx)}
            tabIndex={0}
            onKeyDown={onNudgeKey(idx)}
            onContextMenu={(e) => onContextMenu(e, idx)}
          >
            <div style={{ position: 'relative', width: '100%', height: '100%' }}>
              <div
                title="Drag to reposition"
                onMouseDown={(e) => onStartDrag(idx, e)}
                style={{ position: 'absolute', top: 2, left: 2, width: 12, height: 12, cursor: 'move', zIndex: 12, borderRadius: 2, background: 'rgba(0,0,0,0.15)' }}
              />
            </div>
            {resizing && resizing.idx === idx && (
              <div style={{ 
                position: 'absolute', 
                right: 0, 
                bottom: '100%', 
                marginBottom: 4, 
                background: 'rgba(0,0,0,0.65)', 
                color: '#fff', 
                fontSize: 10, 
                padding: '2px 4px', 
                borderRadius: 3, 
                pointerEvents: 'none' 
              }}>
                {Math.round(width)}×{Math.round(height)}
              </div>
            )}
            {field.type === 'textarea' ? (
              <textarea
                id={field.name}
                name={field.name}
                value={safeValue}
                onChange={onInputChange}
                style={{ fontSize, textAlign, padding:0, width: '100%', height: '100%', background: 'transparent', border: 'none', outline: 'none', borderRadius: 2, boxSizing: 'border-box', resize: 'none' }}
                onFocus={() => onMouseEnter(idx)}
                onBlur={() => onMouseLeave(idx)}
                onKeyDown={onNudgeKey(idx)}
                placeholder={field.name}
              />
            ) : field.type === 'number' ? (
              <FormattedNumberInput
                id={field.name}
                name={field.name}
                value={String(safeValue ?? '')}
                onCanonicalChange={(name, canonical) => {
                  onInputChange({
                    target: { name, value: canonical, type: 'text' },
                  } as React.ChangeEvent<HTMLInputElement>);
                }}
                style={{ fontSize, textAlign, padding:0, width: '100%', height: '100%', background: 'transparent', border: 'none', outline: 'none', borderRadius: 2, boxSizing: 'border-box' }}
                onFocus={() => onMouseEnter(idx)}
                onBlur={() => onMouseLeave(idx)}
                onKeyDown={onNudgeKey(idx)}
              />
            ) : (
              <input
                id={field.name}
                name={field.name}
                type={field.type}
                value={safeValue}
                onChange={onInputChange}
                style={{ fontSize, textAlign, padding:0, width: '100%', height: '100%', background: 'transparent', border: 'none', outline: 'none', borderRadius: 2, boxSizing: 'border-box' }}
                onFocus={() => onMouseEnter(idx)}
                onBlur={() => onMouseLeave(idx)}
                onKeyDown={onNudgeKey(idx)}
                placeholder={field.name}
              />
            )}
            {selectedIdxs.size <= 1 && isSelected && (
              <div
                title="Resize"
                onMouseDown={(e) => onStartResize(idx, 'se', e)}
                style={{ position:'absolute', right:0, bottom:0, width: Math.max(10, Math.min(16, Math.floor(Math.min(width, height)))) , height: Math.max(10, Math.min(16, Math.floor(Math.min(width, height)))) , cursor:'nwse-resize', zIndex:12, pointerEvents:'auto', background:'transparent' }}
              />
            )}
          </div>
        );
      })}
      {/* Signature personnel selector modal */}
      <PersonnelSelect
        open={sigOpen}
        multi
        mode="code_only"
        onCancel={() => { setSigOpen(false); setSigFieldIdx(null); }}
        onOk={(codes: string[] | string) => {
          try {
            const arr = Array.isArray(codes) ? codes : [String(codes)];
            const json = JSON.stringify(arr);
            if (sigFieldIdx !== null) {
              const fld = fields[sigFieldIdx];
              if (onSetFieldDefault) onSetFieldDefault(fld.name, json);
            }
          } catch {}
          setSigOpen(false);
          setSigFieldIdx(null);
        }}
      />
    </>
  );
};

export default FormFieldRenderer;